/**
 * AARSHAVEDA COLLECTION PAGE JS
 * Handles: filter toggling, AJAX sorting/filtering, collapsible groups, AJAX pagination
 */

class AarshavedaCollection {
  constructor() {
    this.container = document.querySelector('.av-collection');
    this.toggleBtn = document.querySelector('.av-collection-toggle-filters');
    this.sortSelect = document.getElementById('SortBy-Custom');
    this.filterForm = document.getElementById('AV-FacetFiltersForm');
    this.gridContainer = document.getElementById('ProductGridContainer');
    this.sidebarContainer = document.getElementById('main-collection-filters');

    if (!this.container) return;

    this.init();
    this.initPopstate();
  }

  init() {
    // --- 1. Sidebar toggle logic (Preserving original UI logic exactly) ---
    if (this.toggleBtn) {
      this.toggleBtn.addEventListener('click', () => this.toggleFilters());
      
      if (window.innerWidth <= 989) {
        this.updateBtnText(true);
      } else {
        const isCollapsed = localStorage.getItem('av-filters-collapsed') === 'true';
        if (isCollapsed) {
          this.container.classList.add('av-collection--collapsed');
          this.updateBtnText(true);
        } else {
          this.container.classList.remove('av-collection--collapsed');
          this.updateBtnText(false);
        }
      }
    }

    // --- 2. Sort: AJAX update ---
    if (this.sortSelect) {
      this.sortSelect.addEventListener('change', (e) => {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', e.target.value);
        this.renderPage(url.toString());
      });
    }

    // --- 3. Filter inputs: AJAX update ---
    this.initSidebarListeners();

    // --- 4. Pagination: AJAX update ---
    this.initPaginationListeners();

    // --- 5. Grid/List View Toggles (Preserving original UI logic) ---
    const viewBtns = document.querySelectorAll('.av-collection-view-btn');
    viewBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        viewBtns.forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        if (btn.getAttribute('title') === 'List View') {
          this.container.classList.add('av-collection--list-view');
        } else {
          this.container.classList.remove('av-collection--list-view');
        }
      });
    });
  }

  initPopstate() {
    window.addEventListener('popstate', (e) => {
      if (e.state && e.state.url) {
        this.renderPage(e.state.url, false);
      } else {
        this.renderPage(window.location.href, false);
      }
    });
  }

  toggleFilters() {
    if (window.innerWidth <= 989) {
      const isOpen = this.container.classList.toggle('av-mobile-filters-open');
      this.updateBtnText(!isOpen);
    } else {
      const isNowCollapsed = this.container.classList.toggle('av-collection--collapsed');
      localStorage.setItem('av-filters-collapsed', isNowCollapsed);
      this.updateBtnText(isNowCollapsed);
    }
    window.dispatchEvent(new Event('resize'));
  }

  updateBtnText(isCollapsed) {
    const textNode = this.toggleBtn.querySelector('.av-toggle-text');
    if (textNode) {
      textNode.textContent = isCollapsed ? 'Show Filters' : 'Hide Filters';
    }
  }

  submitFilters() {
    const formData = new FormData(this.filterForm);
    const params = new URLSearchParams(formData);
    
    // Preserve current sort
    if (this.sortSelect && !params.has('sort_by')) {
      params.set('sort_by', this.sortSelect.value);
    }

    const url = `${window.location.pathname}?${params.toString()}`;
    this.renderPage(url);
  }

  async renderPage(url, pushState = true) {
    this.container.classList.add('loading');
    
    try {
      const response = await fetch(url);
      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      // 1. Update Grid
      const newGrid = html.getElementById('ProductGridContainer');
      if (newGrid && this.gridContainer) {
        this.gridContainer.innerHTML = newGrid.innerHTML;
        this.initPaginationListeners();
      }

      // 2. Update Sidebar (Preserving collapsible group states)
      const newSidebar = html.getElementById('main-collection-filters');
      if (newSidebar && this.sidebarContainer) {
        // Capture current collapsed states
        const collapsedGroups = [];
        this.sidebarContainer.querySelectorAll('.av-filter-group.collapsed').forEach(group => {
          const title = group.querySelector('.av-filter-group__title')?.textContent;
          if (title) collapsedGroups.push(title);
        });

        this.sidebarContainer.innerHTML = newSidebar.innerHTML;
        
        // Re-apply collapsed states
        if (collapsedGroups.length > 0) {
          this.sidebarContainer.querySelectorAll('.av-filter-group').forEach(group => {
            const title = group.querySelector('.av-filter-group__title')?.textContent;
            if (collapsedGroups.includes(title)) {
              group.classList.add('collapsed');
            }
          });
        }

        this.filterForm = document.getElementById('AV-FacetFiltersForm');
        this.initSidebarListeners();
      }

      // 3. Update Product Count
      const newCount = html.querySelector('.av-collection-count');
      const currentCount = this.container.querySelector('.av-collection-count');
      if (newCount && currentCount) {
        currentCount.innerHTML = newCount.innerHTML;
      }

      // 4. Update History
      if (pushState) {
        history.pushState({ url }, '', url);
      }

      // 5. Close mobile filters after applying (User requested for mobile view)
      if (window.innerWidth <= 989 && this.container.classList.contains('av-mobile-filters-open')) {
        this.toggleFilters();
      }

    } catch (error) {
      console.error('Failed to load collection AJAX:', error);
      window.location.href = url;
    } finally {
      this.container.classList.remove('loading');
    }
  }

  initSidebarListeners() {
    if (!this.filterForm) return;

    this.filterForm.querySelectorAll('input').forEach(input => {
      if (input.type === 'checkbox') {
        input.addEventListener('change', () => this.submitFilters());
      } else if (input.type === 'number') {
        let timer;
        input.addEventListener('input', () => {
          clearTimeout(timer);
          timer = setTimeout(() => this.submitFilters(), 600);
        });
      }
    });

    this.filterForm.querySelectorAll('.av-filter-group__header').forEach(header => {
      header.addEventListener('click', () => {
        const group = header.closest('.av-filter-group');
        if (group) group.classList.toggle('collapsed');
      });
    });
  }

  initPaginationListeners() {
    if (this.gridContainer) {
      this.gridContainer.querySelectorAll('.pagination__item, .av-collection-empty a').forEach(link => {
        if (link.tagName === 'A') {
          link.addEventListener('click', (e) => {
            e.preventDefault();
            this.renderPage(link.getAttribute('href'));
          });
        }
      });
    }
  }
}

// Init
document.addEventListener('DOMContentLoaded', () => {
  window.avCollection = new AarshavedaCollection();
});

document.addEventListener('shopify:section:load', () => {
  window.avCollection = new AarshavedaCollection();
});
